// src/fieldProgress/harvestComplete.js — A BIG INSURED FIELD WITH NO HARVEST PASS BLOCKS "ALL IN".
//
// Kevin, 2026-10-20, on Silsby Settlement Farms' CY2026 Niagara soybeans: "I would say
// Silsby is not done". The row read ✓ ALL IN, "Every insured field has a harvest pass", at
// 87% of 672 insured acres — while insured field 7112/838/1 (59.4 ac) had no John Deere pass
// of any kind and 7112/4168/1 (12.5 ac) had only a 0.0-ac graze. hClaimWatch called a row
// COMPLETE when 95% of its FIELDS had a pass, so one 59-acre field among many slipped
// under the 5% field allowance.
//
// THE RULE. A field BLOCKS completion when
//   its planted insured acres > max(BLOCK_MIN_AC, BLOCK_MIN_ROW_SHARE x the row's planted
//   insured acres)        — big enough to matter; slivers and edge CLUs never block, AND
//   its machine-harvested acres < BLOCK_HARVESTED_SHARE x its planted insured acres
//                         — no real pass on it (headland trim never gets near half a field).
// Any blocking field makes the row not complete, whatever the 95% field count says. The
// per-field 40% "has a pass" bar and the 0.93 acre floor are unchanged; this is the case
// they could not see.
//
// "Planted insured acres" = insured minus prevent plant, the same denominator the pace uses;
// a wholly-PP field is not passed in at all (it is ground there was never a crop on).
//
// CommonJS: api/_jd.js require()s it, the checker loads it as-is.
"use strict";

const BLOCK_HARVESTED_SHARE = 0.5;
const BLOCK_MIN_AC = 5;
const BLOCK_MIN_ROW_SHARE = 0.02;

const r1 = (x) => Math.round((Number(x) || 0) * 10) / 10;

// fields: [{ fsn, tract, field, planted_ac, harv_ac }] (harvestable fields only)
function blockingHarvestFields(fields, rowPlantedAcres) {
  const floor = Math.max(BLOCK_MIN_AC, BLOCK_MIN_ROW_SHARE * (Number(rowPlantedAcres) || 0));
  return (fields || [])
    .filter((f) => (Number(f.planted_ac) || 0) > floor && (Number(f.harv_ac) || 0) < BLOCK_HARVESTED_SHARE * (Number(f.planted_ac) || 0))
    .map((f) => ({ fsn: f.fsn, tract: f.tract, field: f.field,
                   insured_acres: r1(f.planted_ac), harvested_acres: r1(f.harv_ac) }))
    .sort((a, b) => b.insured_acres - a.insured_acres);
}

// One line per blocking field, for the card: "7112/838/1 — 59.4 ac, no harvest pass".
function blockingFieldLabel(f) {
  const id = [f.fsn, f.tract, f.field].filter((x) => x != null && x !== "").join("/");
  const h = Number(f.harvested_acres) || 0;
  return `${id} — ${r1(f.insured_acres)} ac, ${h >= 0.05 ? `only ${r1(h)} ac harvested` : "no harvest pass"}`;
}

// ⚠ THE % A READER SEES IS OVER THE SAME ACRES THE PACE IS. hClaimWatch paces harvest over
// PLANTED insured acres (insured minus prevent plant) and sends them as `pace_acres`; the
// page divided by `insured_acres`, so Silsby's Niagara soybeans read 87% (587.6 / 672.3)
// while 59.4 of those acres are reported prevent plant and will never see a combine — the
// planted figure is 96% (587.6 / 612.9). Kevin: "It should just be a given that field
// progress represents planted acres and not PP" — so the page shows PLANTED acres, unlabelled
// as to prevent plant, and never insured-with-PP as the headline.
// A row with no `pace_acres` (the county-level path, which paces over insured acres
// itself) falls back to insured, so the page never disagrees with the server's own ratio.
function harvestDenominator(a) {
  if (a && a.pace_acres != null && Number.isFinite(Number(a.pace_acres))) return Number(a.pace_acres);
  return Number(a && a.insured_acres) || 0;
}

module.exports = { BLOCK_HARVESTED_SHARE, BLOCK_MIN_AC, BLOCK_MIN_ROW_SHARE, blockingHarvestFields, blockingFieldLabel, harvestDenominator };
